import { Link } from 'react-router-dom';
import { FileText, Home, ChevronRight } from 'lucide-react';

const TermsPageEN = () => {
  return (
    <div data-testid="terms-page-en" className="min-h-screen bg-white pt-20">
      {/* Breadcrumb */}
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="container-custom py-3">
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            <Link to="/en" className="flex items-center gap-1 text-slate-500 hover:text-blue-600 transition-colors">
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium">Terms and Conditions</span>
          </nav>
        </div>
      </div>
      
      {/* Header */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="container-custom">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
              <FileText className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight']">
                Terms and Conditions of Use
              </h1>
              <p className="text-slate-500 mt-1">Last updated: February 13, 2026</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-slate max-w-none">
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">1. Information About the Operator</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              The AlbMove Platform is operated by <strong>C.S.M. Sh.p.k.</strong>, a limited liability company registered at the National Business Center in the Republic of Albania with:
            </p>
            <ul className="list-none space-y-2 mb-6 bg-slate-50 rounded-xl p-6">
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Tax ID (NIPT):</span> <span className="text-slate-600">M21612019M</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Registration Date:</span> <span className="text-slate-600">12/04/2022</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Administrator:</span> <span className="text-slate-600">Altin Shamku</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Headquarters:</span> <span className="text-slate-600">Rruga Muçaj, Building 1, 1032, Vorë, Tirana, Albania</span></li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              Hereinafter referred to as the "Company" or "AlbMove".
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              These Terms constitute a contractual agreement between the User and C.S.M. Sh.p.k.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">2. Definitions</h2>
            <p className="text-slate-600 leading-relaxed mb-4">For the purposes of these Terms:</p>
            <ul className="space-y-3 mb-6">
              <li className="text-slate-600"><strong className="text-slate-800">"Platform"</strong> includes the website, mobile application, APIs, backend, management system, as well as the network of charging stations integrated through OCPP.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"User"</strong> is any natural or legal person who registers or uses the services.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"Account"</strong> is the personal profile created on the Platform.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"Wallet"</strong> is the electronic credit system where 1 Credit = 1 Lek (ALL).</li>
              <li className="text-slate-600"><strong className="text-slate-800">"Charging Station"</strong> is any charging device integrated into the system.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"Charging Session"</strong> is the period from activation to completion of charging.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"Operator"</strong> is the entity that owns or manages a station connected to the AlbMove system.</li>
              <li className="text-slate-600"><strong className="text-slate-800">"OCPP"</strong> is the Open Charge Point Protocol.</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">3. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed mb-4">By using the Platform, the User:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Declares to have legal capacity according to the Civil Code of the Republic of Albania;</li>
              <li className="text-slate-600">Fully accepts these Terms;</li>
              <li className="text-slate-600">Agrees that these Terms have binding contractual value.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Company reserves the right to modify these Terms at any time, in accordance with Albanian consumer protection legislation (Law No. 9902, dated 17.04.2008 "On Consumer Protection", as amended).
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">4. Account Creation and Verification</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 Individual Accounts</h3>
            <p className="text-slate-600 leading-relaxed mb-4">The User must provide accurate and complete data, in accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">The Civil Code of the Republic of Albania</li>
              <li className="text-slate-600">Law No. 9887/2008 "On Personal Data Protection", as amended</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">The Company has the right to request identification documents for verification.</p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Business Accounts</h3>
            <p className="text-slate-600 leading-relaxed mb-4">Legal entities must submit:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Valid Tax ID (NIPT)</li>
              <li className="text-slate-600">Registration documentation</li>
              <li className="text-slate-600">Legal representative information</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">Verification may be done in accordance with Albanian fiscal legislation.</p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.3 Suspension</h3>
            <p className="text-slate-600 leading-relaxed mb-4">The Company has the right to suspend or close the account in case of:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">False information</li>
              <li className="text-slate-600">Suspicion of fraud</li>
              <li className="text-slate-600">Violation of these Terms</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">5. Digital Wallet and Payments</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.1 Credit System</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">The Wallet operates as an electronic prepayment system.</li>
              <li className="text-slate-600">Transactions constitute payment for energy and management services.</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.2 Fiscalization</h3>
            <p className="text-slate-600 leading-relaxed mb-4">All transactions are fiscalized in accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Law No. 87/2019 "On Invoices and the Circulation Monitoring System"</li>
              <li className="text-slate-600">Relevant bylaws</li>
              <li className="text-slate-600">Tax Administration guidelines</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">Fiscal invoices are automatically generated through the DevPOS system.</p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">6. Pricing Structure</h2>
            <p className="text-slate-600 leading-relaxed mb-4">The Company applies dynamic pricing in accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Market conditions</li>
              <li className="text-slate-600">Energy costs</li>
              <li className="text-slate-600">Operator's tariff policy</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-slate-700 font-semibold mb-2">Pricing Snapshot</p>
              <p className="text-slate-600">The price displayed at the start of the session constitutes an offer accepted by the User and remains unchanged for that session.</p>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">7. Responsibility for Station Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">The User is responsible for:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Safe use of equipment</li>
              <li className="text-slate-600">Compliance with technical instructions</li>
              <li className="text-slate-600">Compliance with safety rules</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">The Company is not liable for damages arising from:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">Vehicle defects</li>
              <li className="text-slate-600">Improper installations</li>
              <li className="text-slate-600">Improper use</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">8. Technical Failures</h2>
            <p className="text-slate-600 leading-relaxed mb-4">In case of technical interruption:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Billing is made only for energy consumed</li>
              <li className="text-slate-600">Unfair tariffs do not apply</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">The Company is not liable for interruptions caused by:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">Electric grid operator</li>
              <li className="text-slate-600">Force majeure</li>
              <li className="text-slate-600">Cyber attacks</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">9. Refunds</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Refunds are reviewed in accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Consumer protection law</li>
              <li className="text-slate-600">Principles of proportionality</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">Decision is made within 5 business days.</p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed mb-4">In accordance with the Civil Code:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">The Company is only liable for proven direct damages.</li>
              <li className="text-slate-600">Maximum liability is limited to the amount paid by the User during the last 12 months.</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">11. Force Majeure</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              In accordance with the relevant articles of the Civil Code, the Company is exempt from liability in case of force majeure.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">12. Data Protection and GDPR</h2>
            <p className="text-slate-600 leading-relaxed mb-4">AlbMove processes personal data in accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Law No. 9887/2008 "On Personal Data Protection"</li>
              <li className="text-slate-600">Regulation (EU) 2016/679 (GDPR), when applicable</li>
              <li className="text-slate-600">Principles of lawfulness, proportionality, and minimization</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">The User has the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Access</li>
              <li className="text-slate-600">Rectification</li>
              <li className="text-slate-600">Erasure</li>
              <li className="text-slate-600">Restriction of processing</li>
              <li className="text-slate-600">Objection</li>
              <li className="text-slate-600">Data portability</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Requests should be sent to: <a href="mailto:support@albmove.al" className="text-blue-600 hover:underline">support@albmove.al</a>
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">13. Jurisdiction</h2>
            <p className="text-slate-600 leading-relaxed mb-4">These Terms are governed by the laws of the Republic of Albania.</p>
            <p className="text-slate-600 leading-relaxed mb-6">Any dispute is subject to the exclusive jurisdiction of the Courts of Tirana.</p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">14. Final Provisions</h2>
            <p className="text-slate-600 leading-relaxed mb-4">If any provision is declared invalid, the rest remains in force.</p>
            <p className="text-slate-600 leading-relaxed mb-8">These Terms constitute the complete agreement between the User and C.S.M. Sh.p.k.</p>
            
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPageEN;
